import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { ArrowLeft } from "react-feather";
import ProductDetails from "./ProductDetails";
import PrimaryDocument from "./PrimaryDocument";
import TransactionTable from "./TransactionTable";
import { displayToast } from "../utils/Toast";
import { useSalesFormStore } from "../services/useSalesFormStore";
import { useErrorHandler } from "../services/ErrorHandler";
import Edit from "../assets/svgIcons/FormEdit.svg";
import Enquiry from "../assets/svgIcons/Enquiry.svg";
import Print from "../assets/svgIcons/FormPrint.svg";
import Copy from "../assets/svgIcons/Copy.svg";
import Delete from "../assets/svgIcons/FormDelete.svg";
import Quotation from "../assets/svgIcons/Quotation.svg";
import Loader from "./Loader";
import { getFieldMaster } from "../apis/fieldMaster";
import {
  fetchProductMasterTransactionData,
  fetchTransactionById,
  deleteTransactionByCode,
  fetchTransactionSettingsByCode,
  fetchBomByProduct,
} from "../apis/transaction";
import { usePermissions } from "../hooks/usePermissions";
import ConfirmationModal from "./ConfirmationModal";
import "../styles/pages/formBulder/SalesForm.css";

const BOM = () => {
  const { tab: urlTab, category } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { handleErrorResponse } = useErrorHandler();
  const company = location.state?.company;
  // Unstable `?? {}` here re-triggered every [productData] effect and froze the page.
  const productData = useMemo(
    () => location.state?.productData ?? {},
    [location.state],
  );
  const {
    transactionId,
    rowData: rowDataFromQuotation,
    dynamicHeadData,
    dynamicItemData,
    dynamicTransaction,
    documentFileName,
    transactionCode: passedTransactionCode,
    headFields: passedHeadFields,
    extraForms: passedExtraForms,
    primaryDetails: passedPrimaryDetails,
    transactionItems: passedTransactionItems,
    customerDetails: passedCustomerDetails,
    isViewMode: passedIsViewMode,
    isEditMode: passedIsEditMode,
    isCopyMode: passedIsCopyMode,
  } = location.state || {};
  const transactionCode = passedTransactionCode === "WO" ? "WBOM" : "BOM";
  const isFromWO = passedTransactionCode === "WO";
  const woTranId = isFromWO ? transactionId : null;
  const wbomTranId = isFromWO ? passedPrimaryDetails?.bom : null;
  const {
    selectedPickUpTransaction,
    selectedTransaction,
    selectedPickUpTransactionItems,
    clearTransactionState,
  } = useSalesFormStore();

  const [tableData, setTableData] = useState({
    head_fields: [],
    rows: [],
    extra_forms: [],
  });
  const [transactionData, setTransactionData] = useState({
    companyData: {},
    headDetails: {},
    itemDetails: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isCleared, setIsCleared] = useState(false);
  const [isCopyMode, setIsCopyMode] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [wbomExists, setWbomExists] = useState(false);
  const [isAutoTransactionNo, setIsAutoTransactionNo] = useState(false);
  const tab =
    urlTab ||
    (window.location.pathname.startsWith("/master") ? "master" : undefined);

  const setPrimaryDetails = useSalesFormStore(
    (state) => state.setPrimaryDetails,
  );
  const setTransactionItems = useSalesFormStore(
    (state) => state.setTransactionItems,
  );
  const clearForm = useSalesFormStore((state) => state.clearForm);

  const rights = usePermissions(transactionCode);
  const canEdit = rights.can_edit || isFromWO;
  const canPrint = rights.can_print || isFromWO;

  useEffect(() => {
    if (rowDataFromQuotation) {
      setIsCopyMode(true);
      setIsEditMode(false);

      const headDetails = rowDataFromQuotation.head_data || {};
      const itemDetails = rowDataFromQuotation.item_data || [];

      setTransactionData({
        companyData: {},
        headDetails,
        itemDetails,
      });

      setPrimaryDetails(headDetails);
      setTransactionItems(itemDetails);

      displayToast(
        "Quotation data loaded. You can modify and save as new.",
        "info",
      );
    }
  }, [rowDataFromQuotation]);

  useEffect(() => {
    if (!productData.tran_id) {
      clearForm();
      clearTransactionState();
      setIsCleared(true);
    } else {
      setIsCleared(true);
    }
  }, [location.state]);

  useEffect(() => {
    if (transactionCode) {
      fetchFieldMaster();
      if (
        !passedPrimaryDetails ||
        Object.keys(passedPrimaryDetails).length === 0 ||
        isFromWO
      ) {
        clearForm();
      }
    }
    if (isFromWO && wbomTranId) {
      fetchWbomData(wbomTranId);
    } else if (isFromWO && woTranId) {
      setPrimaryDetails({ wo_tran_id: Number(woTranId) });
      prefillBomFromProduct();
    } else if (productData && transactionCode && productData.bom_created) {
      fetchFieldMasterTransactionData(transactionCode, productData.tran_id);
    }
  }, [productData, transactionCode]);

  useEffect(() => {
    if (!transactionCode) return;

    let isActive = true;
    const getTransactionSettings = async () => {
      try {
        const settings = await fetchTransactionSettingsByCode(transactionCode);
        if (isActive) {
          setIsAutoTransactionNo(Boolean(settings?.is_auto_transaction_no));
        }
      } catch (error) {
        console.error(
          "Failed to fetch transaction settings for auto numbering:",
          error,
        );
      }
    };

    getTransactionSettings();

    return () => {
      isActive = false;
    };
  }, [transactionCode]);

  useEffect(() => {
    if (isFromWO) return;
    if (passedPrimaryDetails && Object.keys(passedPrimaryDetails).length > 0) {
      setPrimaryDetails(passedPrimaryDetails);
    }
    if (passedTransactionItems && passedTransactionItems.length > 0) {
      setTransactionItems(passedTransactionItems);
    }
  }, [passedPrimaryDetails, passedTransactionItems]);

  useEffect(() => {
    if (
      isCleared &&
      selectedPickUpTransaction &&
      selectedPickUpTransaction[selectedTransaction.tran_code]?.tran_id
    ) {
      const id =
        selectedPickUpTransaction[selectedTransaction.tran_code]?.tran_id;

      fetchFieldMasterTransactionData(transactionCode, productData.tran_id);
    }
  }, [selectedPickUpTransaction, isCleared]);

  const processAndApplyDynamicData = () => {
    if (!dynamicTransaction) {
      return;
    }

    if (dynamicTransaction.head_fields && dynamicHeadData) {
      const updatedHeadDetails = { ...transactionData.headDetails };

      dynamicTransaction.head_fields.forEach((mapping) => {
        const sourceField = mapping.field_name;
        const targetField = mapping.copy_to_field_name;

        if (dynamicHeadData[sourceField] !== undefined) {
          updatedHeadDetails[targetField] = dynamicHeadData[sourceField];
        }
      });

      setTransactionData((prev) => ({
        ...prev,
        headDetails: updatedHeadDetails,
      }));
      setPrimaryDetails(updatedHeadDetails);
    }

    if (
      dynamicTransaction.item_fields &&
      Array.isArray(dynamicItemData) &&
      dynamicItemData.length > 0
    ) {
      let updatedItemDetails = [];

      updatedItemDetails = dynamicItemData.map((sourceItem) => {
        const newItem = {};

        dynamicTransaction.item_fields.forEach((mapping) => {
          const sourceField = mapping.field_name;
          const targetField = mapping.copy_to_field_name;

          if (sourceItem[sourceField] !== undefined) {
            newItem[targetField] = sourceItem[sourceField];
          }
        });

        return newItem;
      });

      setTransactionData((prev) => ({
        ...prev,
        itemDetails: updatedItemDetails,
      }));
      setTransactionItems(updatedItemDetails);
    }

    displayToast("Dynamic data has been applied", "info");
  };

  useEffect(() => {
    if (
      dynamicTransaction &&
      (dynamicHeadData || (dynamicItemData && dynamicItemData.length > 0))
    ) {
      processAndApplyDynamicData();
    }
  }, [dynamicTransaction, dynamicHeadData, dynamicItemData]);

  const handleCloseEditMode = () => {
    setIsCopyMode(false);
    setIsEditMode(false);
    if (transactionCode && productData.tran_id) {
      fetchFieldMasterTransactionData(transactionCode, productData.tran_id);
    }
  };

  const prefillBomFromProduct = async () => {
    const productId =
      passedPrimaryDetails?.product_id ||
      (Array.isArray(passedTransactionItems)
        ? passedTransactionItems.find((item) => item?.product_id)?.product_id
        : null);

    if (!productId) return;

    try {
      const response = await fetchBomByProduct(Number(productId));
      if (
        !response ||
        response.description ||
        response.error ||
        !Array.isArray(response.item_data) ||
        response.item_data.length === 0
      ) {
        return;
      }

      const copiedItems = response.item_data.map((item) => {
        const { id, tran_id, created_at, updated_at, ...rest } = item;
        return rest;
      });

      setTransactionItems(copiedItems);
      setTransactionData((prev) => ({
        ...prev,
        itemDetails: copiedItems,
      }));
      displayToast("BOM loaded from product \u2014 editable copy", "info");
    } catch (error) {
    }
  };

  const fetchWbomData = async (wbomId) => {
    setPrimaryDetails({});
    setIsLoading(true);
    try {
      const response = await fetchTransactionById("WBOM", wbomId);
      console.log("fetchWbomData response:", response);
      if (response.description) {
        setPrimaryDetails({ wo_tran_id: Number(woTranId) });
        setWbomExists(false);
      } else {
        const companyData = response.company_data || {};
        const headDetails = response.head_data || {};
        const itemDetails = response.item_data || [];

        setTransactionData({
          companyData,
          headDetails,
          itemDetails,
        });
        setPrimaryDetails({
          ...headDetails,
          wo_tran_id: Number(woTranId),
        });
        setTransactionItems(itemDetails);
        setWbomExists(true);
      }
    } catch (error) {
      setPrimaryDetails({ wo_tran_id: Number(woTranId) });
      setWbomExists(false);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFieldMasterTransactionData = async (
    transactionCode,
    productDataId,
  ) => {
    setPrimaryDetails({});

    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await fetchProductMasterTransactionData(
        transactionCode,
        productDataId,
      );
      if (response.description) {
        if (isFromWO && woTranId) {
          setPrimaryDetails({ wo_tran_id: woTranId });
        }
        setWbomExists(false);
      } else {
        const companyData = response.company_data || {};
        const headDetails = response.head_data || {};
        const itemDetails = response.item_data || [];

        if (productDataId) {
          setTransactionData({
            companyData,
            headDetails,
            itemDetails,
          });
          setPrimaryDetails({
            ...headDetails,
            ...(isFromWO ? { wo_tran_id: woTranId } : {}),
          });
          setTransactionItems(itemDetails);
          if (isFromWO) setWbomExists(true);
        } else if (selectedPickUpTransactionItems?.length > 0) {
          setPrimaryDetails(headDetails);
        }
      }
    } catch (error) {
      displayToast("Failed to fetch field master transaction data", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFieldMaster = async () => {
    if (isLoading) return;
    setIsLoading(true);

    try {
      const response = await getFieldMaster({
        transactionCode: transactionCode,
      });
      if (response.description) {
        handleErrorResponse(response.description);

        displayToast(response.description, "error");
      } else {
        const headFields = response.items?.head_fields || [];
        const rows = response.items?.item_fields || [];
        const extraForms = response.items?.extra_forms || [];

        const updatedHeadFields = [...headFields];
        setTableData({
          head_fields: updatedHeadFields,
          rows,
          extra_forms: extraForms,
        });
      }
    } catch (error) {
      displayToast("Failed to fetch field master data", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrintClick = () => {
    if (!canPrint) {
      displayToast(`You don't have permission to print ${category}`, "error");
      return;
    }

    if (
      !transactionData.headDetails ||
      Object.keys(transactionData.headDetails).length === 0
    ) {
      displayToast("No transaction data available to print", "error");
      return;
    }

    const headDetailsWithTranCode = {
      ...transactionData.headDetails,
      tran_code:
        transactionData.headDetails.tran_code || transactionCode || "BOM",
    };

    navigate(`/${tab}/${category}/transactions/transactionInvoice`, {
      state: {
        formData: {
          headDetails: headDetailsWithTranCode,
          itemDetails: transactionData.itemDetails || [],
          companyData: transactionData.companyData || {},
          rowData: tableData?.rows || [],
          transactionCode: transactionCode,
        },
      },
    });
  };

  const handleCopyClick = () => {
    setIsCopyMode(true);
    setIsEditMode(false);
    setPrimaryDetails(transactionData.headDetails);
    setTransactionItems(transactionData.itemDetails);
    displayToast(
      "Form is now in copy mode. You can modify and save as a new transaction.",
      "info",
    );
  };

  const handleEditClick = () => {
    if (!canEdit) {
      displayToast(`You don't have permission to edit ${category}`, "error");
      return;
    }

    setIsEditMode(true);
    setIsCopyMode(false);
    setPrimaryDetails(transactionData.headDetails);
    setTransactionItems(transactionData.itemDetails);
    displayToast(
      "Form is now in edit mode. You can modify and update the transaction.",
      "info",
    );
  };

  const handleDeleteClick = () => {
    if (!rights.can_delete) {
      displayToast(`You don't have permission to delete ${category}`, "error");
      return;
    }
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteTransactionByCode(
        transactionCode,
        transactionId,
      );

      if (response.description) {
        handleErrorResponse(response.description);
        displayToast(response.description, "error");
      } else {
        displayToast("Transaction deleted successfully", "success");
        navigate(`/${tab}/${category}`, {
          state: { transactionCode, forceRefresh: true },
        });
      }
    } catch (error) {
      console.error("Error deleting transaction:", error);
      displayToast("Failed to delete transaction", "error");
    } finally {
      setShowDeleteConfirmation(false);
    }
  };

  const isViewMode = isFromWO
    ? wbomExists && !isCopyMode && !isEditMode
    : Boolean(transactionId) && !isCopyMode && !isEditMode;

  // const isViewMode =
  //   (Boolean(transactionId) && !isCopyMode && !isEditMode) ||
  //   productData.bom_created === "success";

  const handleBackClick = () => {
    navigate(-1);
  };

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <div className="sales-form-container">
          <header className="sales-form-header">
            <div className="sales-form-header-heading">
              <button
                className="back-button-transaction"
                onClick={handleBackClick}
              >
                <ArrowLeft size={25} />
              </button>
              <img
                src={category === "enquiry" ? Enquiry : Quotation}
                className="sales-form-header-icon"
                alt="Form Type"
              />
              <h5>
                BOM(Bill of Material) {isCopyMode && " - Copy Mode"}
                {isEditMode && " - Edit Mode"}
              </h5>
            </div>
            <div className="sales-form-header-heading">
              {isCopyMode || isEditMode ? (
                <>
                  <img
                    src={Print}
                    className="sales-form-icons"
                    onClick={handlePrintClick}
                    style={{
                      cursor: canPrint ? "pointer" : "not-allowed",
                      opacity: canPrint ? 1 : 0.5,
                    }}
                    title={
                      canPrint ? "Print Transaction" : "No permission to print"
                    }
                    alt="Print"
                  />
                  <X
                    className="sales-form-icons cursor-pointer"
                    onClick={handleCloseEditMode}
                    size={24}
                    title="Close Mode"
                  />
                </>
              ) : isViewMode ? (
                <>
                  <img
                    src={Edit}
                    className="sales-form-icons"
                    alt="Edit"
                    onClick={handleEditClick}
                    style={{
                      cursor: canEdit ? "pointer" : "not-allowed",
                      opacity: canEdit ? 1 : 0.5,
                    }}
                    title={
                      canEdit ? "Edit Transaction" : "No permission to edit"
                    }
                  />
                  <img
                    src={Print}
                    className="sales-form-icons"
                    onClick={handlePrintClick}
                    style={{
                      cursor: canPrint ? "pointer" : "not-allowed",
                      opacity: canPrint ? 1 : 0.5,
                    }}
                    title={
                      canPrint ? "Print Transaction" : "No permission to print"
                    }
                    alt="Print"
                  />
                </>
              ) : (
                <>
                  {transactionCode && transactionId && !isFromWO && (
                    <>
                      <img
                        src={Copy}
                        className="sales-form-icons"
                        alt="Copy"
                        onClick={handleCopyClick}
                        style={{
                          cursor: rights.can_add ? "pointer" : "not-allowed",
                          opacity: rights.can_add ? 1 : 0.5,
                        }}
                        title={
                          rights.can_add
                            ? "Copy Transaction"
                            : "No permission to copy"
                        }
                      />
                      <img
                        src={Edit}
                        className="sales-form-icons"
                        alt="Edit"
                        onClick={handleEditClick}
                        style={{
                          cursor: canEdit ? "pointer" : "not-allowed",
                          opacity: canEdit ? 1 : 0.5,
                        }}
                        title={
                          canEdit ? "Edit Transaction" : "No permission to edit"
                        }
                      />
                      <img
                        src={Delete}
                        className="sales-form-icons"
                        alt="Delete"
                        onClick={handleDeleteClick}
                        style={{
                          cursor: rights.can_delete ? "pointer" : "not-allowed",
                          opacity: rights.can_delete ? 1 : 0.5,
                        }}
                        title={
                          rights.can_delete
                            ? "Delete Transaction"
                            : "No permission to delete"
                        }
                      />
                    </>
                  )}
                  <img
                    src={Print}
                    className="sales-form-icons"
                    onClick={handlePrintClick}
                    style={{
                      cursor: canPrint ? "pointer" : "not-allowed",
                      opacity: canPrint ? 1 : 0.5,
                    }}
                    title={
                      canPrint ? "Print Transaction" : "No permission to print"
                    }
                    alt="Print"
                  />
                </>
              )}
            </div>
          </header>
          <div className="sales-form-content">
            <ProductDetails
              tab={tab}
              company={company ? company : transactionData.companyData}
              isEditMode={isEditMode}
              productData={{
                ...productData,
                name:
                  productData?.name ||
                  passedPrimaryDetails?.product_name ||
                  passedTransactionItems?.[0]?.product_name ||
                  "",
                description:
                  productData?.description ||
                  passedPrimaryDetails?.product_desc ||
                  passedTransactionItems?.[0]?.product_description ||
                  passedTransactionItems?.[0]?.product_desc ||
                  "",
              }}
              isFromWO={isFromWO}
              customerName={
                company?.company_name ||
                transactionData.companyData?.company_name ||
                passedCustomerDetails?.company_name
              }
              workOrderNo={
                passedPrimaryDetails?.tran_no ||
                transactionData.headDetails?.tran_no
              }
            />

            <PrimaryDocument
              transactionCode={transactionCode}
              headFields={tableData.head_fields}
              extraForms={tableData.extra_forms}
              isViewMode={isViewMode}
              isCopyMode={isCopyMode}
              isInEditMode={isEditMode}
              isAutoTransactionNo={isAutoTransactionNo}
            />
          </div>
          <TransactionTable
            rowData={tableData?.rows || []}
            transactionCode={transactionCode || ""}
            isViewMode={isViewMode}
            initialData={
              isCopyMode || isEditMode ? transactionData.itemDetails : undefined
            }
            isEditMode={isEditMode}
            setIsEditMode={setIsEditMode}
            headFields={tableData.head_fields}
            locationState={location.state}
          />
        </div>
      )}

      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeleteConfirm}
        message="Are you sure you want to delete this transaction?"
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
};

export default BOM;
